import { sendGTMEvent } from "@next/third-parties/google"
import { useEffect, useMemo, useState } from "react"

import { sendGoogleAnalyticsEvent } from "@modules/common/utils/analytics"
import OnfidoVerification from "@modules/identity-verification/components/onfido"
import VerifyInWhatsappScreen from "@modules/signup-and-order-flow/components/verify-in-whatsapp-screen"

import VerificationOptions from "./verification-options"
import VerifyInAppScreen from "./verify-in-app-screen"
import { SignupFlowType, WhatsappIdentityVerificationType } from "../index"
import WhatsappIdentityVerification from "./whatsapp-identity-verification"
import { CheckoutExperimentsDefinition } from "../experiments/experiment-interfaces"
import useProductData from "../util/use-fetch-product-data"
import useSendWhatsappLink from "../util/use-send-onfido-link"
import { useGetProfile } from "../util/useGetProfile"

enum Step {
  IdentityVerificationOption = "Identity Verification Options",
  OnfidoVerification = "Identity Verification web",
  VerifyInApp = "Continue verification in app",
  WhatsappVerification = "whatsapp_verification_link",
  NativeWhatsappIdentityVerification = "Native Whatsapp Identity Verification",
}

const PURCHASE_INTENT_INTERSTITIAL_LINK = "https://6i6nb.app.link/9KiyKmevDRb"
const APP_DOWNLOAD_LINK = "https://a.nelo.mx/ZpywgahKiMb"

export default function VerificationStack({
  authToken,
  onComplete,
  webVerificationPath,
  productId,
  handleCloseSheet,
  whatsappIdentityVerification,
  treatments,
}: {
  authToken: string
  onComplete: () => void
  webVerificationPath: SignupFlowType | null
  productId: string | null
  handleCloseSheet: () => void
  whatsappIdentityVerification?: WhatsappIdentityVerificationType
  treatments: CheckoutExperimentsDefinition
}) {
  const [step, setStep] = useState<Step>(Step.IdentityVerificationOption)
  const [eventLogged, setEventLogged] = useState(false)

  const { product } = useProductData(productId)
  const { profile } = useGetProfile()

  const truncatedProductTitle = product?.title ? (product.title.length > 20 ? `${product.title.slice(0, 20)}...` : product.title) : ""

  const { sendWhatsappVerificationLink, error: whatsappError, isWhatsappLinkLoading } = useSendWhatsappLink()

  const appDownloadLink = useMemo(() => {
    try {
      const intentLink = new URL(PURCHASE_INTENT_INTERSTITIAL_LINK)
      intentLink.searchParams.append("phone", profile?.phoneNumber || "")
      intentLink.searchParams.append("email", profile?.email || "")
      intentLink.searchParams.append("product_thumbnail", product?.thumbnail || "")
      intentLink.searchParams.append("product_title", product?.title || "")

      return treatments.mktp_purchase_intent_interstitial.config ? intentLink.toString() : APP_DOWNLOAD_LINK
    } catch (error) {
      console.error("Error creating purchase intent interstitial link:", error)
      return APP_DOWNLOAD_LINK
    }
  }, [profile, product, treatments.mktp_purchase_intent_interstitial.config])

  useEffect(() => {
    sendGoogleAnalyticsEvent("XP_INCLUSION", {
      featureName: "purchase_intent_interstitial",
      trackingKey: "purchase_intent_interstitial_experiment",
      treatmentName: treatments.mktp_purchase_intent_interstitial.config,
    })
  }, [treatments])

  useEffect(() => {
    if (whatsappIdentityVerification === "true") {
      setStep(Step.NativeWhatsappIdentityVerification)
    }
  }, [whatsappIdentityVerification])

  useEffect(() => {
    if (eventLogged) return
    sendGoogleAnalyticsEvent("XP_INCLUSION", {
      featureName: "whatsapp_identity_verification",
      trackingKey: "whatsapp_identity_verification_experiment",
      treatmentName: whatsappIdentityVerification,
    })
    setEventLogged(true)
  }, [whatsappIdentityVerification, eventLogged])

  const onShouldVerifyInWeb = () => {
    setStep(Step.OnfidoVerification)
  }

  const onShouldVerifyInApp = () => {
    setStep(Step.VerifyInApp)
  }

  const onShouldVerifyInWhatsapp = async () => {
    try {
      await sendWhatsappVerificationLink(truncatedProductTitle)
      setStep(Step.WhatsappVerification)
    } catch (error) {
      console.error("Error sending WhatsApp verification link:", error)
    }
  }

  useEffect(() => {
    sendGTMEvent({
      event: step,
    })
  }, [step])

  useEffect(() => {
    sendGoogleAnalyticsEvent("VERIFICATION_STACK", {
      step: step,
    })
  }, [step])

  const renderStep = () => {
    switch (step) {
      case Step.IdentityVerificationOption:
        return (
          <VerificationOptions
            onShouldVerifyInWeb={onShouldVerifyInWeb}
            onShouldVerifyInApp={onShouldVerifyInApp}
            onShouldVerifyInWhatsapp={onShouldVerifyInWhatsapp}
            verificationPath={webVerificationPath}
            isWhatsappLinkLoading={isWhatsappLinkLoading}
            whatsappError={whatsappError}
          />
        )
      case Step.OnfidoVerification:
        return <OnfidoVerification authToken={authToken} onComplete={onComplete} />
      case Step.VerifyInApp:
        return <VerifyInAppScreen appDownloadLink={appDownloadLink} />
      case Step.WhatsappVerification:
        return <VerifyInWhatsappScreen />
      case Step.NativeWhatsappIdentityVerification:
        return (
          <WhatsappIdentityVerification
            userProfile={profile}
            sendWhatsappLink={sendWhatsappVerificationLink}
            downloadApp={onShouldVerifyInApp}
            handleCloseSheet={handleCloseSheet}
            productTitle={truncatedProductTitle}
          />
        )
    }
  }

  return <div>{renderStep()}</div>
}
